import { Image } from "expo-image";
import { type ImageSourcePropType, Pressable } from "react-native";
import { Drawer } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import type { NavItem } from "./types";

type LogoVariants = {
	light: ImageSourcePropType;
	dark: ImageSourcePropType;
};

type Props = {
	logo: {
		default: LogoVariants;
		stacked: LogoVariants;
	};
	items: NavItem[];
	activeItemKey?: string;
	isCollapsed?: boolean;
	renderFab?: () => React.ReactNode;
	renderFooter?: () => React.ReactNode;
	onPressItem: (key: string) => void;
	onPressAccount: () => void;
	onPressLogo?: () => void;
};

type NavRailItemProps = {
	collapsed: boolean;
	active?: boolean;
	label: string;
	icon?: NavItem["icon"];
	activeIcon?: NavItem["activeIcon"];
	onPress: () => void;
};

const NavRailItem = ({
	collapsed,
	active,
	label,
	icon,
	activeIcon,
	onPress,
}: NavRailItemProps) => {
	if (collapsed) {
		return (
			<Drawer.CollapsedItem
				active={active}
				onPress={onPress}
				label={label}
				focusedIcon={activeIcon}
				unfocusedIcon={icon}
				style={{ marginHorizontal: 0 }}
			/>
		);
	}
	return (
		<Drawer.Item
			active={active}
			onPress={onPress}
			label={label}
			icon={active ? (activeIcon ?? icon) : icon}
			style={{ marginHorizontal: 0 }}
		/>
	);
};

const NavigationRail = ({
	logo,
	items,
	activeItemKey,
	isCollapsed = false,
	renderFab,
	renderFooter,
	onPressItem,
	onPressAccount,
	onPressLogo,
}: Props) => {
	const theme = useAppTheme();
	const insets = useSafeAreaInsets();

	const logoVariant = isCollapsed ? logo.stacked : logo.default;
	const finalLogo = theme.dark ? logoVariant.dark : logoVariant.light;

	return (
		<Box
			height="100%"
			backgroundColor={theme.colors.surface}
			alignItems={isCollapsed ? "center" : "stretch"}
			justifyContent="space-between"
			gap={16}
			paddingHorizontal={isCollapsed ? 0 : 16}
			paddingTop={insets.top + 16}
			paddingBottom={16}
		>
			<Box gap={8}>
				<Pressable
					onPress={onPressLogo}
					accessibilityRole="link"
					accessibilityLabel="Go to home"
				>
					<Box
						width={isCollapsed ? 50 : 136}
						height={56}
						style={{
							marginLeft: isCollapsed ? "auto" : 0,
							marginRight: isCollapsed ? "auto" : 0,
						}}
					>
						<Image
							contentFit="contain"
							source={finalLogo}
							transition={400}
							style={{
								width: "100%",
								height: "100%",
							}}
							alt="App Logo"
							accessibilityLabel="App Logo"
						/>
					</Box>
				</Pressable>
				<Box flex={1} gap={40}>
					{renderFab && (
						<Box alignItems={isCollapsed ? "center" : "flex-start"} gap={6}>
							{renderFab()}
						</Box>
					)}
					<Box flex={1} flexDirection="column" gap={6}>
						{items.map((item) => (
							<NavRailItem
								key={item.key}
								collapsed={isCollapsed}
								active={activeItemKey === item.key}
								label={item.label}
								icon={item.icon}
								activeIcon={item.activeIcon}
								onPress={() => onPressItem(item.key)}
							/>
						))}
					</Box>
				</Box>
			</Box>
			<Box gap={16}>
				{!isCollapsed && (
					<Box
						marginHorizontal={16}
						borderTopWidth={1}
						borderTopColor={theme.colors.border}
					/>
				)}
				<NavRailItem
					collapsed={isCollapsed}
					active={activeItemKey === "account"}
					label="Account"
					icon="account-outline"
					activeIcon="account"
					onPress={onPressAccount}
				/>
				{renderFooter?.()}
			</Box>
		</Box>
	);
};

export { NavigationRail };
